import { type Channel } from '@commercetools/platform-sdk'
import { type CheckedState } from '@radix-ui/react-checkbox'
import { useTranslation } from 'react-i18next'
import PlentyBody from '#app/components/typography/plenty-body'
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from '#app/components/ui/accordion'
import { Checkbox } from '#app/components/ui/checkbox'
import { Label } from '#app/components/ui/label'
import { type SelectedStoresCookie } from '.'

const StoreListForm = ({
	municipalities,
	selectedStores,
	setSelectedStores,
}: {
	municipalities: { [key: string]: Channel[] }
	selectedStores: any[]
	setSelectedStores: any
}) => {
	let { t } = useTranslation('product_list_page')

	const kommuneChecked = (stores: Channel[]): CheckedState => {
		if (
			stores.every(store => selectedStores.find(st => st.key === store.key))
		) {
			return true
		} else if (
			stores.some(store => selectedStores.find(st => st.key === store.key))
		) {
			return 'indeterminate'
		} else {
			return false
		}
	}

	const handleSelectAllStores = (value: boolean, stores: Channel[]) => {
		if (value) {
			setSelectedStores((prevSelectedStores: SelectedStoresCookie[]) => [
				...prevSelectedStores,
				...stores.map(store => ({
					name: store.name?.en,
					key: store.key,
				})),
			])
		} else {
			setSelectedStores((prevSelectedStores: SelectedStoresCookie[]) => [
				...prevSelectedStores.filter(
					st => !stores.find(store => store.key === st.key),
				),
			])
		}
	}

	const handleSelectStore = (value: boolean, store: Channel) => {
		if (value) {
			setSelectedStores((prevSelectedStores: SelectedStoresCookie[]) => [
				...prevSelectedStores,
				{
					name: store.name?.en,
					key: store.key,
				},
			])
		} else {
			setSelectedStores((prevSelectedStores: SelectedStoresCookie[]) => [
				...prevSelectedStores.filter(st => st.key !== store.key),
			])
		}
	}

	return (
		<Accordion type="multiple" className="w-full py-4 md:py-0">
			{Object.entries(municipalities)
				.sort()
				.map(([municipality, stores]) => {
					return (
						<AccordionItem
							key={municipality}
							value={municipality}
							className="py-2"
						>
							<div className="flex w-full flex-row items-start gap-x-2">
								<Checkbox
									onCheckedChange={(value: boolean) =>
										handleSelectAllStores(value, stores)
									}
									checked={kommuneChecked(stores)}
								></Checkbox>
								<AccordionTrigger className="flex w-full flex-row items-center justify-between py-0 md:py-0">
									<div className="flex flex-row items-center gap-x-2">
										<PlentyBody size="md">
											{t('store_list_form.all_stores_in', { municipality })}
										</PlentyBody>
									</div>
								</AccordionTrigger>
							</div>
							<div className="mb-2 pl-8">
								<PlentyBody className="text-slate-400" size="sm">
									{t('store_list_form.municipality_stores_count', {
										storesCount: stores.length,
									})}
								</PlentyBody>
							</div>
							<AccordionContent className="flex flex-col gap-2 py-2 pl-8">
								{(stores as Channel[]).map(store => (
									<div
										key={store.key}
										className="flex flex-row items-center gap-x-2"
									>
										<Checkbox
											className="h-5 w-5"
											onCheckedChange={value => handleSelectStore(value, store)}
											checked={
												selectedStores.find(st => st.key === store.key)
													? true
													: false
											}
											id={store.key}
											name={store.key}
										/>
										<Label className="cursor-pointer" htmlFor={store.key}>
											<PlentyBody size="sm">
												{`${store.name?.en}, ${store.address?.postalCode} `}
											</PlentyBody>
										</Label>
									</div>
								))}
							</AccordionContent>
						</AccordionItem>
					)
				})}
		</Accordion>
	)
}
export default StoreListForm
